export type ConfigFormRow = {
  keyId: string;
  value: string;
  isDefault?: boolean;
};

export type ConfigSettingCategory =
  | "interactive map"
  | "style"
  | "features"
  | "messaging"
  | "performance";

export type ConfigSettingDefault = {
  keyId: string;
  defaultValue: string;
  description?: string;
  category: ConfigSettingCategory;
};

type ConfigLike = {
  key?: string | null;
  keyId?: string | null;
  value?: unknown;
};

export const CONFIG_SETTINGS_DEFAULTS: ConfigSettingDefault[] = [
  {
    keyId: "APP_ROLES",
    defaultValue: `["MapMaker", "Preview"]`,
    description: "JSON array of role names that can be assigned to users (e.g. for map editing or preview access)",
    category: "features",
  },
  {
    keyId: "SHOWCASE_ARTIST_TIMES",
    defaultValue: "false",
    description: "Show artist set times on the Showcase screen",
    category: "features",
  },
  {
    keyId: "ENABLED_SCREENS",
    defaultValue: "",
    description: "Comma-separated list of screen names that are enabled in the app's navigation",
    category: "features",
  },
  {
    keyId: "ENABLE_REVIEW_PROMPT",
    defaultValue: "false",
    description: "Show the app store review prompt to users",
    category: "features",
  },
  {
    keyId: "NEWS_POST_LINKS",
    defaultValue: "true",
    description: "Show links on news post detail screens",
    category: "features",
  },
  {
    keyId: "COLOR_MEANINGS",
    defaultValue: "true",
    description: "Show the colour meanings / priorities legend on the Clashfinder settings screen",
    category: "style",
  },
  {
    keyId: "LINEUP_ARTIST_TIMES",
    defaultValue: "true",
    description: "Show artist set times on the band detail screen",
    category: "features",
  },
  {
    keyId: "ARTIST_BUTTONS_ENABLED",
    defaultValue: "true",
    description: "Show the artist action buttons (e.g. links/socials) on the band detail screen",
    category: "features",
  },
  {
    keyId: "BAND_TICKET_ICON_ENABLED",
    defaultValue: "true",
    description: "Show a ticket icon on bands that have a ticket button/link",
    category: "features",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOW_CAR",
    defaultValue: "true",
    description: "Show car park markers on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOW_FOOD",
    defaultValue: "true",
    description: "Show food vendor markers on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOW_INFO",
    defaultValue: "true",
    description: "Show info point markers on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOW_MERCH",
    defaultValue: "true",
    description: "Show merchandise stand markers on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOW_OTHER",
    defaultValue: "true",
    description: "Show markers tagged as \"other\" on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOW_VENDORS",
    defaultValue: "true",
    description: "Show trader/vendor markers on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOW_STAGES",
    defaultValue: "true",
    description: "Show stage markers on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOW_TENT",
    defaultValue: "true",
    description: "Show tent/camping markers on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOW_BAR",
    defaultValue: "true",
    description: "Show bar markers on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOW_BARS_SEPARATELY",
    defaultValue: "true",
    description: "List bars as their own category/filter rather than grouped with other markers",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOW_INFO_SEPARATELY",
    defaultValue: "false",
    description: "List info points as their own category/filter rather than grouped with other markers",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOWCASE_STAGES",
    defaultValue: "false",
    description: "Show showcase stages as a distinct marker group on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "AREA_CAPACITY",
    defaultValue: `{"areas":[],"lastUpdated":null}`,
    description: "JSON payload of area capacity data (areas and last updated timestamp) shown on the Area Capacity card/screen",
    category: "features",
  },
  {
    keyId: "windowSize",
    defaultValue: "5",
    description: "Home screen FlatList windowsize",
    category: "performance",
  },
  {
    keyId: "initialNumToRender",
    defaultValue: "5",
    description: "Home screen FlatList initialNumToRender",
    category: "performance",
  },
  {
    keyId: "AREA_CAPACITY_MENU_LABEL",
    defaultValue: "Area Capacity",
    description: "Label shown on the Area Capacity card/menu item",
    category: "style",
  },
  {
    keyId: "NEWS_MENU_LABEL",
    defaultValue: "News",
    description: "Label shown on the News menu item",
    category: "style",
  },
  {
    keyId: "NEWS_MENU_ICON",
    defaultValue: "newspaper",
    description: "Ionicons icon name used for the News menu item",
    category: "style",
  },
  {
    keyId: "LINEUP_MENU_LABEL",
    defaultValue: "Lineup",
    description: "Label shown on the Lineup menu item",
    category: "style",
  },
  {
    keyId: "LINEUP_MENU_ICON",
    defaultValue: "list",
    description: "Ionicons icon name used for the Lineup menu item",
    category: "style",
  },
  {
    keyId: "ARTIST_MENU_LABEL",
    defaultValue: "Artists",
    description: "Label shown on the Artists/Showcase menu item",
    category: "style",
  },
  {
    keyId: "ARTIST_MENU_ICON",
    defaultValue: "star",
    description: "Ionicons icon name used for the Artists/Showcase menu item",
    category: "style",
  },
  {
    keyId: "CLASHFINDER_MENU_LABEL",
    defaultValue: "Clashfinder",
    description: "Label shown on the Clashfinder menu item",
    category: "style",
  },
  {
    keyId: "CLASHFINDER_MENU_ICON",
    defaultValue: "calendar-sharp",
    description: "Ionicons icon name used for the Clashfinder menu item",
    category: "style",
  },
  {
    keyId: "MAP_MENU_LABEL",
    defaultValue: "Map",
    description: "Label shown on the Map menu item",
    category: "style",
  },
  {
    keyId: "MAP_MENU_ICON",
    defaultValue: "map-outline",
    description: "Ionicons icon name used for the Map menu item",
    category: "style",
  },
  {
    keyId: "BAR_MENU_LABEL",
    defaultValue: "Bar",
    description: "Label shown on the Bar menu item",
    category: "style",
  },
  {
    keyId: "BAR_MENU_ICON",
    defaultValue: "beer",
    description: "Ionicons icon name used for the Bar menu item",
    category: "style",
  },
  {
    keyId: "FOOD_MENU_LABEL",
    defaultValue: "Food",
    description: "Label shown on the Food menu item",
    category: "style",
  },
  {
    keyId: "FOOD_MENU_ICON",
    defaultValue: "fast-food",
    description: "Ionicons icon name used for the Food menu item",
    category: "style",
  },
  {
    keyId: "TRADERS_MENU_LABEL",
    defaultValue: "Traders",
    description: "Label shown on the Traders menu item",
    category: "style",
  },
  {
    keyId: "TRADERS_MENU_ICON",
    defaultValue: "cart",
    description: "Ionicons icon name used for the Traders menu item",
    category: "style",
  },
  {
    keyId: "SPONSORS_MENU_LABEL",
    defaultValue: "Sponsors",
    description: "Label shown on the Sponsors menu item",
    category: "style",
  },
  {
    keyId: "SPONSORS_MENU_ICON",
    defaultValue: "heart",
    description: "Ionicons icon name used for the Sponsors menu item",
    category: "style",
  },
  {
    keyId: "INFO_MENU_LABEL",
    defaultValue: "Info",
    description: "Label shown on the Info menu item",
    category: "style",
  },
  {
    keyId: "INFO_MENU_ICON",
    defaultValue: "information",
    description: "Ionicons icon name used for the Info menu item",
    category: "style",
  },
  {
    keyId: "SETTINGS_MENU_LABEL",
    defaultValue: "Settings",
    description: "Label shown on the Settings menu item",
    category: "style",
  },
  {
    keyId: "SETTINGS_MENU_ICON",
    defaultValue: "camera",
    description: "Ionicons icon name used for the Settings menu item",
    category: "style",
  },
  {
    keyId: "NOTIFICATIONS_MENU_LABEL",
    defaultValue: "Notifications",
    description: "Label shown on the Notifications menu item",
    category: "style",
  },
  {
    keyId: "NOTIFICATIONS_MENU_ICON",
    defaultValue: "camera",
    description: "Ionicons icon name used for the Notifications menu item",
    category: "style",
  },
  {
    keyId: "REMINDERS_MENU_LABEL",
    defaultValue: "Reminders",
    description: "Label shown on the Reminders menu item",
    category: "style",
  },
  {
    keyId: "REMINDERS_MENU_ICON",
    defaultValue: "camera",
    description: "Ionicons icon name used for the Reminders menu item",
    category: "style",
  },
  {
    keyId: "SHARE_MENU_LABEL",
    defaultValue: "Share",
    description: "Label shown on the Share menu item",
    category: "style",
  },
  {
    keyId: "SHARE_MENU_ICON",
    defaultValue: "camera",
    description: "Ionicons icon name used for the Share menu item",
    category: "style",
  },
  {
    keyId: "SHARE_CLASHFINDERS_MENU_LABEL",
    defaultValue: "Share Clashfinders",
    description: "Label shown on the Share Clashfinders menu item",
    category: "style",
  },
  {
    keyId: "SHARE_CLASHFINDERS_MENU_ICON",
    defaultValue: "camera",
    description: "Ionicons icon name used for the Share Clashfinders menu item",
    category: "style",
  },
  {
    keyId: "LOGIN_MENU_LABEL",
    defaultValue: "Login",
    description: "Label shown on the Login menu item",
    category: "style",
  },
  {
    keyId: "LOGIN_MENU_ICON",
    defaultValue: "camera",
    description: "Ionicons icon name used for the Login menu item",
    category: "style",
  },
  {
    keyId: "VENDOR_HIDE_LABEL",
    defaultValue: "false",
    description: "Hide the vendor/trader name label on trader cards",
    category: "style",
  },
  {
    keyId: "SHOWCASE_HIDE_LABEL",
    defaultValue: "false",
    description: "Hide the showcase label on the Showcase screen",
    category: "style",
  },
  {
    keyId: "SHOWCASE_USE_BOOKINGS",
    defaultValue: "false",
    description: "Populate the Showcase screen from all lineup bookings instead of the curated showcase bands list",
    category: "features",
  },
  {
    keyId: "SHOWCASE_RECYCLER_VIEW",
    defaultValue: "false",
    description: "Use the alternative recycler-list layout for the band detail screen",
    category: "performance",
  },
  {
    keyId: "SPONSORS_HIDE_LABEL",
    defaultValue: "false",
    description: "Hide the sponsors label on the Sponsors screen",
    category: "style",
  },
  {
    keyId: "STAGE_FILTERS",
    defaultValue: "false",
    description: "Show stage filters on the Clashfinder settings screen",
    category: "features",
  },
  {
    keyId: "PREFETCH_MAPS",
    defaultValue: "false",
    description: "Prefetch map images on the Home screen for faster loading",
    category: "performance",
  },
  {
    keyId: "USE_LOCAL_MAPS",
    defaultValue: "false",
    description: "Use locally bundled map images instead of remote map URLs",
    category: "performance",
  },
  {
    keyId: "LINEUP_GENRE_SELECTOR_ENABLED",
    defaultValue: "false",
    description: "Show the genre selector filter on the Lineup screen",
    category: "features",
  },
  {
    keyId: "LINEUP_DATE_SELECTOR_DISABLED",
    defaultValue: "false",
    description: "Hide the date selector on the Lineup screen",
    category: "features",
  },
  {
    keyId: "BAND_NOTES_ENABLED",
    defaultValue: "false",
    description: "Enable personal notes on band detail screens",
    category: "features",
  },
  {
    keyId: "BAND_NOTES_EXPORT_ENABLED",
    defaultValue: "false",
    description: "Enable exporting band notes from Settings",
    category: "features",
  },
  {
    keyId: "CLASHFINDER_ROTATION_ENABLED",
    defaultValue: "true",
    description: "Allow the Clashfinder grid to rotate between horizontal and vertical layouts",
    category: "features",
  },
  {
    keyId: "SHARE_CLASHFINDER_REQUIRES_AUTH",
    defaultValue: "false",
    description: "Require authentication before viewing a shared Clashfinder",
    category: "features",
  },
  {
    keyId: "SPONSOR_NAVIGATION_ENABLED",
    defaultValue: "false",
    description: "Show the sponsor navigation strip in the app's main navigation",
    category: "features",
  },
  {
    keyId: "SPONSOR_NAVIGATION_LABEL",
    defaultValue: "Sponsored by",
    description: "Label text shown above the sponsor navigation logos",
    category: "style",
  },
  {
    keyId: "BAND_SPONSOR_NAVIGATION_ENABLED",
    defaultValue: "false",
    description: "Show the sponsor navigation strip on the band detail screen",
    category: "features",
  },
  {
    keyId: "NEWS_SPONSOR_NAVIGATION_ENABLED",
    defaultValue: "false",
    description: "Show the sponsor navigation strip on news post detail screens",
    category: "features",
  },
  {
    keyId: "SPONSOR_NAV_NAV_IMAGE_SIZE",
    defaultValue: "60",
    description: "Sponsor logo size (px) used in the main navigation sponsor strip",
    category: "style",
  },
  {
    keyId: "SPONSOR_NAV_ALIGN",
    defaultValue: "center",
    description: "Horizontal alignment (flexbox alignItems) of the sponsor strip in the main navigation",
    category: "style",
  },
  {
    keyId: "SPONSOR_NAV_LABEL_GAP",
    defaultValue: "-25",
    description: "Bottom margin (px) applied to the sponsor label in the main navigation sponsor strip",
    category: "style",
  },
  {
    keyId: "SPONSOR_NAV_SCREEN_LABEL_GAP",
    defaultValue: "-25",
    description: "Bottom margin (px) applied to the sponsor label in the in-screen sponsor strip",
    category: "style",
  },
  {
    keyId: "SPONSOR_NAV_SCREEN_IMAGE_SIZE",
    defaultValue: "60",
    description: "Sponsor logo size (px) used in the in-screen sponsor strip (band/news screens)",
    category: "style",
  },
  {
    keyId: "SPONSOR_NAV_SCREEN_ALIGN",
    defaultValue: "center",
    description: "Horizontal alignment (flexbox alignItems) of the in-screen sponsor strip",
    category: "style",
  },
  {
    keyId: "REVIEW_PROMPT_KEY",
    defaultValue: "",
    description: "Identifier used to key the app store review prompt; falls back to the clashfinder name if blank",
    category: "features",
  },
  {
    keyId: "MAP_IMAGE_URL",
    defaultValue: "",
    description: "URL of the static map image shown on the (non-Mapbox) Map screen",
    category: "interactive map",
  },
  {
    keyId: "MAPS",
    defaultValue: "",
    description: "Configuration for multiple selectable static maps on the Map screen",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_DATA",
    defaultValue: "",
    description: "JSON data (markers/areas/etc.) that drives the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_USE_IMAGES",
    defaultValue: "false",
    description: "Use image assets instead of SVGs for markers on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOW_ACCESSIBILITY",
    defaultValue: "false",
    description: "Show accessibility markers on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOWCASE_STAGES_NAME",
    defaultValue: "Showcase",
    description: "Display name used for the showcase stages category on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOWCASE_STAGES_ICON",
    defaultValue: "music",
    description: "Icon name used for showcase stage markers on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOWCASE_STAGES_ITEMS_LABEL",
    defaultValue: "",
    description: "Label used for the list of artists/items within a showcase stage on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOWCASE_SHOW_AREA_LABELS",
    defaultValue: "false",
    description: "Show area name labels for showcase stages on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOWCASE_SHOW_FILTERS",
    defaultValue: "false",
    description: "Show filter controls for showcase stages on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_SHOWCASE_VIEW_ARTISTS_LABEL",
    defaultValue: "View artists",
    description: "Button label used to view artists for a showcase stage on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_GRID_CELL_SIZE_M",
    defaultValue: "100",
    description: "Grid cell size in metres used for the map's location grid overlay",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_DEFAULT_SATELLITE_VIEW",
    defaultValue: "false",
    description: "Default the interactive (Mapbox) map to satellite view instead of standard view",
    category: "interactive map",
  },
  {
    keyId: "INTERACTIVE_MAP_VENDOR_LABEL_MIN_ZOOM",
    defaultValue: "18",
    description: "Minimum zoom level at which vendor labels appear on the interactive (Mapbox) map",
    category: "interactive map",
  },
  {
    keyId: "MAPBOX_VERSION_REQUIRED",
    defaultValue: "",
    description: "Minimum app version required to show the Mapbox map screens",
    category: "interactive map",
  },
  {
    keyId: "SHARE_VERSION_REQUIRED",
    defaultValue: "",
    description: "Minimum app version required to show the Share screen",
    category: "features",
  },
  {
    keyId: "SHAREDCLASHFINDERS_VERSION_REQUIRED",
    defaultValue: "",
    description: "Minimum app version required to show the Shared Clashfinders screen",
    category: "features",
  },
  {
    keyId: "CLASHFINDERSHARING_VERSION_REQUIRED",
    defaultValue: "",
    description: "Minimum app version required to show Clashfinder sharing in Settings",
    category: "features",
  },
  {
    keyId: "AREA_CAPACITIES_VERSION_REQUIRED",
    defaultValue: "",
    description: "Minimum app version required to show the Area Capacities card on Home",
    category: "features",
  },
  {
    keyId: "MAPBOX_OFFLINE_MAPS_VERSION_REQUIRED",
    defaultValue: "",
    description: "Minimum app version required to enable offline Mapbox map downloads",
    category: "interactive map",
  },
  {
    keyId: "APP_UPDATES_VERSION_REQUIRED",
    defaultValue: "",
    description: "Minimum app version required to enable the in-app update prompt",
    category: "features",
  },
  {
    keyId: "COUNTDOWN_TEXT",
    defaultValue: "",
    description: "Text shown above the Home screen countdown timer; defaults to the formatted countdown date",
    category: "messaging",
  },
  {
    keyId: "COUNTDOWN_END_MESSAGE",
    defaultValue: "THANK YOU",
    description: "Message shown on the Home screen once the countdown has ended",
    category: "messaging",
  },
  {
    keyId: "APP_UPDATE_AVAILABLE",
    defaultValue: "",
    description: "Message shown on the Home screen prompting users to update the app",
    category: "messaging",
  },
  {
    keyId: "HOME_MESSAGE",
    defaultValue: "",
    description: "Announcement message shown the first time a user visits the Home screen",
    category: "messaging",
  },
  {
    keyId: "LINEUP_MESSAGE",
    defaultValue: "",
    description: "Announcement message shown the first time a user visits the Lineup screen",
    category: "messaging",
  },
  {
    keyId: "SHARED_CLASHFINDERS_MESSAGE",
    defaultValue: "",
    description: "Announcement message shown the first time a user visits the Shared Clashfinders screen",
    category: "messaging",
  },
  {
    keyId: "CLASHFINDER_MESSAGE",
    defaultValue: "",
    description: "Announcement message shown the first time a user visits the Clashfinder screen",
    category: "messaging",
  },
];

const normalizeConfigValue = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "";
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

export const mergeConfigWithDefaults = (configs: ConfigLike[] = []) => {
  const merged = new Map<string, ConfigFormRow>();

  configs.forEach((item) => {
    const keyId = (item.keyId ?? item.key ?? "").trim();
    if (!keyId) return;

    merged.set(keyId, {
      keyId,
      value: normalizeConfigValue(item.value),
      isDefault: false,
    });
  });

  CONFIG_SETTINGS_DEFAULTS.forEach((setting) => {
    if (merged.has(setting.keyId)) return;

    merged.set(setting.keyId, {
      keyId: setting.keyId,
      value: setting.defaultValue,
      isDefault: true,
    });
  });

  return Array.from(merged.values()).sort((a, b) =>
    a.keyId.localeCompare(b.keyId),
  );
};
